import React, { useState, useMemo, useRef, useEffect } from "react";
import "./StatusFilter.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import type { Invoice } from "../InvoiceTable/InvoiceTable";

type StatusValue = "Un-Paid" | "Partial" | "Paid" | "Void" | null;

type StatusFilterProps = {
  activeStatus: StatusValue | string | null;
  onStatusChange: (status: StatusValue | string | null) => void;
  allData?: Invoice[];
};

type StatusOption = {
  value: StatusValue;
  label: string;
  theme: "all" | "unpaid" | "partial" | "paid" | "void";
};

const StatusFilter: React.FC<StatusFilterProps> = ({
  activeStatus,
  onStatusChange,
  allData,
}) => {
  const [showOtherDropdown, setShowOtherDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const statusOptions: StatusOption[] = [
    { value: null, label: "All", theme: "all" },
    { value: "Un-Paid", label: "Unpaid", theme: "unpaid" },
    { value: "Partial", label: "Partial", theme: "partial" },
    { value: "Paid", label: "Paid", theme: "paid" },
    { value: "Void", label: "Void", theme: "void" },
  ];

  // Extract unique "Other" statuses from the data
  const otherStatuses = useMemo(() => {
    if (!allData || !Array.isArray(allData)) return [];

    const uniqueStatuses = new Set<string>();
    allData.forEach((invoice) => {
      let status: string;
      if (invoice.status === "void") {
        status = "Void";
      } else {
        const amountPaid = parseFloat((invoice.amount_paid as string) || "0");
        const totalAmount = parseFloat((invoice.total_amount as string) || "0");

        if (amountPaid >= totalAmount) {
          status = "Paid";
        } else if (amountPaid > 0) {
          status = "Partial";
        } else {
          status = "Un-Paid";
        }
      }

      if (status && !["Un-Paid", "Partial", "Paid", "Void"].includes(status)) {
        uniqueStatuses.add(status);
      }
    });

    return Array.from(uniqueStatuses).sort();
  }, [allData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowOtherDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleStatusClick = (status: StatusValue | "Other") => {
    if (status === "Other") {
      setShowOtherDropdown(!showOtherDropdown);
      return;
    }

    if (activeStatus === status) {
      onStatusChange(null);
    } else {
      onStatusChange(status);
    }
    setShowOtherDropdown(false);
  };

  const handleOtherStatusSelect = (specificStatus: string) => {
    onStatusChange(specificStatus);
    setShowOtherDropdown(false);
  };

  const hasOtherActive = otherStatuses.includes((activeStatus as string) ?? "");

  return (
    <div className="status-filter">
      <div className="status-filter-buttons">
        {statusOptions.map((option) => (
          <button
            key={option.value ?? "all"}
            className={`status-filter-button status-${option.theme} ${
              activeStatus === option.value ? "active" : ""
            }`}
            onClick={() => handleStatusClick(option.value)}
          >
            {option.label}
          </button>
        ))}

        {otherStatuses.length > 0 && (
          <div className="other-status-container" ref={dropdownRef}>
            <button
              className={`status-filter-button status-all ${hasOtherActive ? "active" : ""}`}
              onClick={() => handleStatusClick("Other")}
            >
              {hasOtherActive ? activeStatus : "Other"}
              <FontAwesomeIcon
                icon={showOtherDropdown ? faChevronUp : faChevronDown}
                className="dropdown-icon"
              />
            </button>
            {showOtherDropdown && (
              <div className="other-status-dropdown">
                {otherStatuses.map((status) => (
                  <button
                    key={status}
                    className={`dropdown-item ${activeStatus === status ? "active" : ""}`}
                    onClick={() => handleOtherStatusSelect(status)}
                  >
                    {status}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusFilter;
